import React from 'react';
import PropTypes from 'prop-types';
import Title from '../../commonComponents/Title';
import SignInForm from './SignInForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import OtpForm from './OtpForm';

const isMethodEnabled = (methods, type) =>
  Array.isArray(methods) &&
  methods.some(m => m && m.type === type && m.enabled === true);
import DismissibleAlert, {
  ALERT_TYPE,
} from '../../commonComponents/forms/DismissibleAlert';
import SignInContainer from './container/SignInContainer';
import translate from '../../commonComponents/translate';

/**
 * Main SignIn page component, wraps the SignInForm and ForgotPasswordForm as well as the
 * success/error alert functionality
 */
class SignIn extends React.Component {
  constructor(props) {
    super(props);

    const authMethods = (props.tenant && props.tenant.authMethods) || [];
    const otpEnabled = isMethodEnabled(authMethods, 'otpEmail');
    const passwordEnabled =
      authMethods.length === 0 || isMethodEnabled(authMethods, 'password');

    this.state = {
      showSignIn: !(otpEnabled && !passwordEnabled),
      showForgotPassword: false,
      showOtp: otpEnabled && !passwordEnabled,
      showAlert: !!props.signInError,
      alertMessage: props.signInError ? props.t(props.signInError) : '',
      alertType: props.signInError ? ALERT_TYPE.DANGER : '',
    };

    this.displaySignInForm = this.displaySignInForm.bind(this);
    this.displayForgotPasswordForm = this.displayForgotPasswordForm.bind(this);
    this.displayOtpForm = this.displayOtpForm.bind(this);
    this.displayAlert = this.displayAlert.bind(this);
    this.hideAlert = this.hideAlert.bind(this);
  }

  displayOtpForm() {
    this.setState({
      showSignIn: false,
      showForgotPassword: false,
      showOtp: true,
      showAlert: false,
    });
  }

  displaySignInForm() {
    this.setState(
      {
        showSignIn: true,
        showForgotPassword: false,
        showOtp: false,
        showAlert: false,
      },
      () =>
        $('#m_login')
          .find('.m-login__signin')
          .animateClass('flipInX animated'),
    );
  }

  displayForgotPasswordForm() {
    this.setState(
      { showSignIn: false, showForgotPassword: true, showAlert: false },
      () =>
        $('#m_login')
          .find('.m-login__forget-password')
          .animateClass('flipInX animated'),
    );
  }

  displayAlert(type, msg) {
    this.setState({ showAlert: true, alertMessage: msg, alertType: type }, () =>
      $('#alertContainer').animateClass('fadeIn animated'),
    );
  }

  hideAlert() {
    this.setState({ showAlert: false, alertMessage: '', alertType: '' });
  }

  render() {
    const {
      t,
      theme,
      organizationData,
      email,
      password,
      logo,
      redirectPath,
      tenant,
    } = this.props;

    const {
      showSignIn,
      showForgotPassword,
      showOtp,
      showAlert,
      alertMessage,
      alertType,
    } = this.state;

    const authMethods = (tenant && tenant.authMethods) || [];
    const otpEnabled = isMethodEnabled(authMethods, 'otpEmail');
    const passwordEnabled =
      authMethods.length === 0 || isMethodEnabled(authMethods, 'password');

    return (
      <Title title={t('login_head_title')}>
        <div
          className="m-grid m-grid--hor m-grid--root m-page"
          style={{ height: '100vh' }}
        >
          <SignInContainer
            theme={theme}
            forgotPassword={showForgotPassword}
            logo={logo}
          >
            {showAlert && (
              <DismissibleAlert
                message={alertMessage}
                type={alertType}
                onClick={this.hideAlert}
                autoDismiss={false}
              />
            )}
            {showSignIn && (
              <SignInForm
                displayForgotPasswordForm={this.displayForgotPasswordForm}
                displayOtpForm={otpEnabled ? this.displayOtpForm : null}
                organizationData={organizationData}
                email={email}
                password={password}
                redirectPath={redirectPath}
              />
            )}
            {showForgotPassword && (
              <ForgotPasswordForm
                displayAlert={this.displayAlert}
                displaySignInForm={this.displaySignInForm}
                alertTypes={ALERT_TYPE}
              />
            )}
            {showOtp && (
              <OtpForm
                onBackToPassword={
                  passwordEnabled ? this.displaySignInForm : null
                }
                showBackToPassword={passwordEnabled}
                redirectPath={redirectPath}
              />
            )}
          </SignInContainer>
        </div>
      </Title>
    );
  }
}

SignIn.propTypes = {
  /** translation function, i18n.__ on the browser, response.__ on the server */
  t: PropTypes.func.isRequired,
  /** Initial error rendered from the backend, replaces the old hasError/error on the ejs */
  signInError: PropTypes.string,
  /** Previously entered email, in case of error */
  email: PropTypes.string,
  /** Previously entered password, in case of error */
  password: PropTypes.string,
  /** Organization data, in case the user belongs to multiple organizations and we need to pick one */
  organizationData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      identifierCode: PropTypes.string.isRequired,
    }).isRequired,
  ),
  /** Selected metronic theme, from the Config */
  theme: PropTypes.string.isRequired,
  logo: PropTypes.string,
  redirectPath: PropTypes.string,
  tenant: PropTypes.shape({
    authMethods: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        enabled: PropTypes.bool,
      }),
    ),
  }),
};

SignIn.defaultProps = {
  signInError: null,
  organizationData: null,
  email: null,
  password: null,
  logo: null,
  redirectPath: '',
  tenant: null,
};

export default translate(SignIn);
